import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { User } from 'wasp/entities';
import {
  type SubscriptionStatus,
  prettyPaymentPlanName,
  parsePaymentPlanId,
} from '../payment/plans';
import {
  getCustomerPortalUrl,
  useQuery,
  useAction,
  updateCurrentUserAboutMe,
  deleteAccount,
  exportUserData,
} from 'wasp/client/operations';
import { routes } from 'wasp/client/router';
import { logout } from 'wasp/client/auth';
import { Button, MenuActionButton } from '../components/common';
import { LinkButton } from '../components/ui';
import { triggerJsonDownload } from '../client/shareLinks';

const ABOUT_MAX_LENGTH = 280;
const ACCOUNT_LABEL_CLASS_NAME =
  'text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400';
const ACCOUNT_ACTION_CLASS_NAME =
  'inline-flex items-center text-sm font-semibold text-primary transition hover:text-primary/80';
const SUPPORT_CARD_ACTION_CLASS_NAME =
  'inline-flex w-full items-center justify-center rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-primary transition hover:bg-white/90 focus:outline-hidden focus:ring-2 focus:ring-white/70';
const LOGOUT_BUTTON_CLASS_NAME =
  'group inline-flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold shadow-sm sm:w-auto sm:min-w-[140px]';

export default function AccountPage({ user }: { user: User }) {
  const [aboutInput, setAboutInput] = useState(user.aboutMe ?? '');
  const [savedAbout, setSavedAbout] = useState(user.aboutMe?.trim() ?? '');
  const [saveState, setSaveState] = useState<
    'idle' | 'saving' | 'success' | 'error'
  >('idle');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isEditingAbout, setIsEditingAbout] = useState(false);
  const aboutTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const updateAboutMe = useAction(updateCurrentUserAboutMe);

  useEffect(() => {
    const normalizedAbout = user.aboutMe?.trim() ?? '';
    setAboutInput(user.aboutMe ?? '');
    setSavedAbout(normalizedAbout);
    setSaveState('idle');
    setStatusMessage(null);
    setIsEditingAbout(false);
  }, [user.aboutMe]);

  useEffect(() => {
    if (!isEditingAbout) {
      return;
    }

    const textarea = aboutTextareaRef.current;
    if (!textarea) {
      return;
    }

    textarea.focus();
    const textLength = textarea.value.length;
    textarea.setSelectionRange(textLength, textLength);
  }, [isEditingAbout]);

  const trimmedInput = aboutInput.trim();
  const normalizedSavedAbout = savedAbout ?? '';
  const aboutCopy =
    normalizedSavedAbout ||
    'You have not shared a status yet. Click edit to share a little about yourself.';
  const hasChanges = trimmedInput !== normalizedSavedAbout;
  const isSaving = saveState === 'saving';
  const aboutCharCount = aboutInput.length;
  const isCharLimitHit = aboutCharCount >= ABOUT_MAX_LENGTH;
  const displayName = user.username?.trim() || 'Optimistic Reader';
  const emailAddress = user.email || 'No email on file';
  const avatarInitial = (
    displayName[0] ||
    emailAddress[0] ||
    'O'
  ).toUpperCase();

  const handleStartEditing = () => {
    setIsEditingAbout(true);
    setAboutInput(savedAbout ?? '');
    setSaveState('idle');
    setStatusMessage(null);
  };

  const handleCancelEditing = () => {
    setIsEditingAbout(false);
    setAboutInput(savedAbout ?? '');
    setSaveState('idle');
    setStatusMessage(null);
  };

  const handleAboutChange = (value: string) => {
    setAboutInput(value);
    if (saveState !== 'idle') {
      setSaveState('idle');
    }
    setStatusMessage(null);
  };

  const handleSaveAboutMe = async () => {
    if (!hasChanges || isSaving) {
      return;
    }

    setSaveState('saving');
    setStatusMessage(null);
    try {
      await updateAboutMe({ aboutMe: trimmedInput });
      setSavedAbout(trimmedInput);
      setAboutInput(trimmedInput);
      setSaveState('success');
      setStatusMessage('Saved!');
      setIsEditingAbout(false);
    } catch (error) {
      console.error('Failed to save profile status', error);
      setSaveState('error');
      setStatusMessage('We could not save your status. Please try again.');
    }
  };

  return (
    <div className="mt-10 px-4 sm:px-6">
      <div className="mx-auto max-w-3xl space-y-4 lg:space-y-6 lg:px-8">
        <section className="overflow-hidden rounded-2xl border border-neutral-900/10 bg-white shadow-lg dark:border-neutral-100/10 dark:bg-boxdark">
          <div className="flex items-center gap-4 border-b border-neutral-900/10 px-5 py-5 dark:border-neutral-100/10 sm:px-6">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-linear-to-br from-primary to-primary/80 text-lg font-semibold text-white">
              {avatarInitial}
            </div>
            <div className="min-w-0">
              <p className="text-base font-semibold text-neutral-900 dark:text-white">
                {displayName}
              </p>
              <p className="truncate text-sm text-neutral-500 dark:text-neutral-400">
                {emailAddress}
              </p>
            </div>
          </div>

          <dl className="divide-y divide-neutral-900/10 px-5 py-1 dark:divide-neutral-100/10 sm:px-6">
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5">
              <dt className={ACCOUNT_LABEL_CLASS_NAME}>Newsletter</dt>
              <dd className="mt-1 flex flex-col items-start gap-1 text-sm text-neutral-900 dark:text-neutral-400 sm:col-span-2 sm:mt-0 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                <span>
                  {user.sendNewsletter
                    ? 'Subscribed to weekly newsletter'
                    : 'Not subscribed'}
                </span>
                <LinkButton
                  to={routes.NewsletterRoute.to}
                  variant="link"
                  tone="primary"
                  className={ACCOUNT_ACTION_CLASS_NAME}
                >
                  {user.sendNewsletter ? 'Manage subscription' : 'Subscribe'}
                </LinkButton>
              </dd>
            </div>

            {/*
              Topics row mirrors the newsletterProviders row structure (String[] on User).
              Follows are managed on ArticlesPage via FollowablePill — this row is
              read-only summary + deep-link, keeping preference management colocated
              with the feed where follows take effect. (ref: DL-005)
            */}
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5">
              <dt className={ACCOUNT_LABEL_CLASS_NAME}>Topics</dt>
              <dd className="mt-1 flex flex-col items-start gap-1 text-sm text-neutral-900 dark:text-neutral-400 sm:col-span-2 sm:mt-0 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                <span>
                  {(user.followedCategories?.length ?? 0) > 0
                    ? `Following ${user.followedCategories.length} topic${
                        user.followedCategories.length === 1 ? '' : 's'
                      }`
                    : 'No topics followed'}
                </span>
                <LinkButton
                  to={routes.ArticlesRoute.to}
                  variant="link"
                  tone="primary"
                  className={ACCOUNT_ACTION_CLASS_NAME}
                >
                  Manage topics
                </LinkButton>
              </dd>
            </div>

            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5">
              <dt className={ACCOUNT_LABEL_CLASS_NAME}>About</dt>
              <dd className="mt-1 text-sm text-neutral-900 dark:text-neutral-400 sm:col-span-2 sm:mt-0">
                <div className="flex flex-col gap-3">
                  {!isEditingAbout ? (
                    <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <p className="text-sm whitespace-pre-line text-neutral-900 dark:text-neutral-400">
                        {aboutCopy}
                      </p>
                      <MenuActionButton
                        type="button"
                        onClick={handleStartEditing}
                        className={`${ACCOUNT_ACTION_CLASS_NAME} shrink-0`}
                      >
                        Edit status
                      </MenuActionButton>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <label
                        htmlFor="account-about-me"
                        className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400"
                      >
                        Status
                      </label>
                      <textarea
                        id="account-about-me"
                        ref={aboutTextareaRef}
                        rows={3}
                        className="w-full rounded-md border border-neutral-300/70 bg-neutral-50/50 px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring focus:ring-primary/30 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white"
                        maxLength={ABOUT_MAX_LENGTH}
                        placeholder="Tell the community a little bit about yourself"
                        value={aboutInput}
                        onChange={(event) =>
                          handleAboutChange(event.target.value)
                        }
                        aria-label="Status message"
                      />
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-xs text-neutral-500 dark:text-neutral-400">
                          <span className={isCharLimitHit ? 'text-danger' : ''}>
                            {aboutCharCount}
                          </span>
                          /{ABOUT_MAX_LENGTH} characters
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            onClick={handleSaveAboutMe}
                            disabled={!hasChanges || isSaving}
                            variant="solid"
                            tone="primary"
                            size="sm"
                            className="inline-flex items-center justify-center px-4 py-2 text-xs font-semibold uppercase tracking-wide"
                          >
                            {isSaving ? 'Saving…' : 'Save status'}
                          </Button>
                          <Button
                            type="button"
                            onClick={handleCancelEditing}
                            variant="outline"
                            tone="neutral"
                            size="sm"
                            className="inline-flex items-center justify-center px-4 py-2 text-xs font-semibold uppercase tracking-wide"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  {statusMessage && (
                    <p
                      role="status"
                      aria-live="polite"
                      className={`text-xs ${
                        saveState === 'error' ? 'text-danger' : 'text-success'
                      }`}
                    >
                      {statusMessage}
                    </p>
                  )}
                </div>
              </dd>
            </div>
          </dl>
        </section>

        <StreakCard
          streakCount={user.streakCount ?? 0}
          bestStreak={user.bestStreak ?? 0}
        />

        <SupportPlanCard
          subscriptionStatus={user.subscriptionStatus as SubscriptionStatus}
          subscriptionPlan={user.subscriptionPlan}
          datePaid={user.datePaid}
        />

        <DataPrivacyCard />

        <DeleteAccountCard />

        <div className="flex w-full justify-center sm:justify-end">
          <Button
            type="button"
            variant="outline"
            tone="neutral"
            size="sm"
            onClick={logout}
            className={LOGOUT_BUTTON_CLASS_NAME}
          >
            <svg
              className="h-4 w-4 text-primary transition group-hover:scale-105"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Log out
          </Button>
        </div>
      </div>
    </div>
  );
}

type StreakCardProps = {
  streakCount: number;
  bestStreak: number;
};

/**
 * Account page streak summary card. Uses a neutral bordered surface to remain
 * visually subordinate to SupportPlanCard (pink gradient) directly below. (ref: DL-008)
 * Shows a prompt to start a streak when streakCount is 0.
 */
function StreakCard({ streakCount, bestStreak }: StreakCardProps) {
  return (
    <section className="overflow-hidden rounded-2xl border border-neutral-900/10 bg-white shadow-lg dark:border-neutral-100/10 dark:bg-boxdark">
      <div className="flex flex-col items-center gap-2 px-5 py-6 text-center sm:px-6">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
          className="text-amber-500 dark:text-amber-400"
        >
          <path d="M13.5 0.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z" />
        </svg>
        {streakCount >= 1 ? (
          <>
            <p className="text-4xl font-bold text-neutral-900 dark:text-white">
              {streakCount}
            </p>
            <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
              day streak
            </p>
          </>
        ) : (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Visit daily to build your streak!
          </p>
        )}
        {bestStreak >= 1 && (
          <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
            Best: {bestStreak} day{bestStreak !== 1 ? 's' : ''}
          </p>
        )}
      </div>
    </section>
  );
}

type SupportPlanCardProps = {
  subscriptionPlan: string | null;
  subscriptionStatus: SubscriptionStatus | null;
  datePaid: Date | null;
};

function SupportPlanCard({
  subscriptionPlan,
  subscriptionStatus,
  datePaid,
}: SupportPlanCardProps) {
  const planName = getSupportPlanName(subscriptionPlan);
  const { description, actionLabel, useCustomerPortal } = getSupportCardContent(
    subscriptionStatus,
    datePaid,
  );
  return (
    <section className="overflow-hidden rounded-2xl bg-linear-to-br from-primary to-primary/80 p-5 text-white shadow-lg shadow-primary/25 sm:p-6">
      <p className="text-xs font-semibold uppercase tracking-[0.09em] text-white/70">
        Your plan
      </p>
      <h3 className="mt-2 text-xl font-semibold">{planName}</h3>
      <p className="mt-2 text-sm leading-6 text-white/85">{description}</p>
      <div className="mt-4">
        {useCustomerPortal ? (
          <CustomerPortalButton
            className={SUPPORT_CARD_ACTION_CLASS_NAME}
            label={actionLabel}
          />
        ) : (
          <LinkButton
            to={routes.PricingPageRoute.to}
            className={SUPPORT_CARD_ACTION_CLASS_NAME}
          >
            {actionLabel}
          </LinkButton>
        )}
      </div>
    </section>
  );
}

function getSupportPlanName(subscriptionPlan: string | null): string {
  if (!subscriptionPlan) {
    return 'Free Plan';
  }

  try {
    return prettyPaymentPlanName(parsePaymentPlanId(subscriptionPlan));
  } catch (error) {
    console.error('Unknown subscription plan id', error);
    return 'Supporter Plan';
  }
}

function getSupportCardContent(
  subscriptionStatus: SubscriptionStatus | null,
  datePaid: Date | null,
) {
  const endOfBillingPeriod = datePaid
    ? prettyPrintEndOfBillingPeriod(datePaid)
    : null;

  switch (subscriptionStatus) {
    case 'active':
      return {
        description:
          'You are actively supporting factual optimism. Thank you for backing independent reporting.',
        actionLabel: 'Manage support',
        useCustomerPortal: true,
      };
    case 'past_due':
      return {
        description:
          'Your support payment is past due. Please update billing details to keep your support active.',
        actionLabel: 'Update billing',
        useCustomerPortal: true,
      };
    case 'cancel_at_period_end':
      return {
        description: `Your recurring support ends soon${
          endOfBillingPeriod || ''
        }. Renew anytime to keep us going.`,
        actionLabel: 'Manage support',
        useCustomerPortal: true,
      };
    case 'paid_once':
      return {
        description:
          'Thanks for the one-time support. If you want to keep us going, you can support us again anytime.',
        actionLabel: 'Support us',
        useCustomerPortal: false,
      };
    case 'deleted':
      return {
        description:
          'Your previous support plan is no longer active. Support us anytime to keep the optimistic news flowing.',
        actionLabel: 'Support us',
        useCustomerPortal: false,
      };
    default:
      return {
        description:
          'Support us to keep factual optimism flowing and unlock supporter perks.',
        actionLabel: 'Support us',
        useCustomerPortal: false,
      };
  }
}

function prettyPrintEndOfBillingPeriod(date: Date) {
  const oneMonthFromNow = new Date(date);
  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
  return ': ' + oneMonthFromNow.toLocaleDateString();
}

function CustomerPortalButton({
  className,
  label,
}: {
  className: string;
  label: string;
}) {
  const {
    data: customerPortalUrl,
    isLoading: isCustomerPortalUrlLoading,
    error: customerPortalUrlError,
  } = useQuery(getCustomerPortalUrl);

  const handleClick = () => {
    if (customerPortalUrlError) {
      console.error('Error fetching customer portal url');
    }

    if (customerPortalUrl) {
      window.open(customerPortalUrl, '_blank');
    } else {
      console.error('Customer portal URL is not available');
    }
  };

  return (
    <Button
      type="button"
      variant="solid"
      tone="neutral"
      size="sm"
      onClick={handleClick}
      disabled={isCustomerPortalUrlLoading}
      className={className}
    >
      {label}
    </Button>
  );
}

function DataPrivacyCard() {
  const [exportState, setExportState] = useState<'idle' | 'loading' | 'error'>(
    'idle',
  );
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = async () => {
    if (exportState === 'loading') {
      return;
    }

    setExportState('loading');
    setExportError(null);

    try {
      const payload = await exportUserData();
      const today = new Date().toISOString().slice(0, 10);
      triggerJsonDownload(payload, `optimistic-news-data-${today}.json`);
      setExportState('idle');
    } catch (error) {
      console.error('Data export failed', error);
      setExportState('error');
      setExportError('Export failed. Please try again.');
    }
  };

  return (
    <section
      id="data-privacy"
      className="overflow-hidden rounded-2xl border border-neutral-900/10 bg-white shadow-lg dark:border-neutral-100/10 dark:bg-neutral-800"
    >
      <div className="px-5 py-5 sm:px-6">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">
          Data &amp; Privacy
        </h3>
        <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
          Export includes: your profile, bookmarks, topics, and activity
        </p>
        <div className="mt-4">
          <Button
            type="button"
            variant="outline"
            tone="neutral"
            size="sm"
            onClick={handleExport}
            disabled={exportState === 'loading'}
            className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
          >
            {exportState === 'loading' ? (
              <>
                <svg
                  className="h-4 w-4 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                Preparing export…
              </>
            ) : (
              <>
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download My Data
              </>
            )}
          </Button>
          {exportState === 'error' && exportError && (
            <p role="alert" className="mt-2 text-xs text-danger">
              {exportError}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

const DELETE_CONFIRMATION_WORD = 'DELETE';

type DeleteStep = 'idle' | 'confirm' | 'deleting' | 'error';

function DeleteAccountCard() {
  const navigate = useNavigate();
  const [step, setStep] = useState<DeleteStep>('idle');
  const [confirmInput, setConfirmInput] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteAccountAction = useAction(deleteAccount);

  const isConfirmed = confirmInput === DELETE_CONFIRMATION_WORD;
  const isDeleting = step === 'deleting';

  const handleBeginConfirm = () => {
    setStep('confirm');
    setConfirmInput('');
    setDeleteError(null);
  };

  const handleCancel = () => {
    setStep('idle');
    setConfirmInput('');
    setDeleteError(null);
  };

  const handleDelete = async () => {
    if (!isConfirmed || isDeleting) {
      return;
    }

    setStep('deleting');
    setDeleteError(null);

    try {
      await deleteAccountAction({});
      // Session is cascade-deleted with the user — logout may fail, but we
      // still need to clear client-side auth state and redirect.
      try {
        await logout();
      } catch {
        // Expected: server session already gone
      }
      navigate(routes.LandingPageRoute.to);
    } catch (error) {
      console.error('Account deletion failed', error);
      setStep('error');
      setDeleteError(
        'We could not delete your account. Please try again or contact support.',
      );
    }
  };

  return (
    <section className="overflow-hidden rounded-2xl border border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950">
      <div className="px-5 py-5 sm:px-6">
        <h3 className="text-sm font-semibold text-red-700 dark:text-red-400">
          Delete Account
        </h3>

        {step === 'idle' && (
          <div className="mt-3 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-red-600/80 dark:text-red-400/80">
              Permanently delete your account and all associated data.
            </p>
            <Button
              type="button"
              variant="outline"
              tone="neutral"
              size="sm"
              onClick={handleBeginConfirm}
              className="shrink-0 inline-flex items-center justify-center rounded-xl border border-red-300 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100 active:bg-red-200 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900 dark:active:bg-red-800"
            >
              Delete My Account
            </Button>
          </div>
        )}

        {step !== 'idle' && (
          <div className="mt-3 space-y-4">
            <ul className="space-y-1.5 text-xs text-red-600/90 dark:text-red-400/80">
              <li className="flex items-start gap-2">
                <span
                  className="mt-0.5 shrink-0 text-red-500"
                  aria-hidden="true"
                >
                  •
                </span>
                Your account and personal data will be permanently deleted
              </li>
              <li className="flex items-start gap-2">
                <span
                  className="mt-0.5 shrink-0 text-red-500"
                  aria-hidden="true"
                >
                  •
                </span>
                Your discussions will be preserved anonymously
              </li>
              <li className="flex items-start gap-2">
                <span
                  className="mt-0.5 shrink-0 text-red-500"
                  aria-hidden="true"
                >
                  •
                </span>
                Active subscriptions will be cancelled
              </li>
            </ul>

            <p className="text-xs text-red-600/80 dark:text-red-400/70">
              Consider{' '}
              <a
                href="#data-privacy"
                className="underline underline-offset-2 transition hover:opacity-80"
              >
                exporting your data first
              </a>{' '}
              (optional) before deleting.
            </p>

            <div className="space-y-2">
              <label
                htmlFor="delete-confirm-input"
                className="block text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-400"
              >
                Type{' '}
                <span className="font-mono">{DELETE_CONFIRMATION_WORD}</span> to
                confirm
              </label>
              <input
                id="delete-confirm-input"
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                disabled={isDeleting}
                autoComplete="off"
                spellCheck={false}
                placeholder={DELETE_CONFIRMATION_WORD}
                className="w-full rounded-md border border-red-300 bg-white px-3 py-2 text-sm text-red-900 placeholder:text-red-300 focus:border-red-500 focus:outline-none focus:ring focus:ring-red-500/30 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-700 dark:bg-red-900/40 dark:text-red-100 dark:placeholder:text-red-700 dark:focus:border-red-500"
              />
            </div>

            {step === 'error' && deleteError && (
              <p
                role="alert"
                className="text-xs text-red-600 dark:text-red-400"
              >
                {deleteError}
              </p>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button
                type="button"
                variant="solid"
                tone="neutral"
                size="sm"
                onClick={handleDelete}
                disabled={!isConfirmed || isDeleting}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 active:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-red-700 dark:hover:bg-red-600 dark:active:bg-red-500"
              >
                {isDeleting ? (
                  <>
                    <svg
                      className="h-4 w-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Deleting…
                  </>
                ) : (
                  'Delete My Account'
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                tone="neutral"
                size="sm"
                onClick={handleCancel}
                disabled={isDeleting}
                className="inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
